import React, { useState } from 'react';
import styled from 'styled-components';
import { Container, Navbar, Nav } from 'react-bootstrap';
import { Link, navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { device } from '../utils/breakpoints';

const HeaderWrapper = styled.header`
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  
  @media ${device.laptop} {
    position: sticky;
    top: 0;
  }
`;

const StyledNavbar = styled(Navbar)`
  padding: 16px 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  
  @media ${device.laptop} {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: center !important;
    justify-content: space-between !important;
    
    .navbar-brand {
      margin-right: auto !important;
      flex-shrink: 0;
    }
    
    .navbar-toggler {
      margin-left: 0 !important;
      flex-shrink: 0;
      order: 2;
    }
  }
  
  @media (min-width: 993px) {
    justify-content: flex-start !important;
    
    .navbar-brand {
      margin-right: 0 !important;
      order: unset !important;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  
  @media ${device.laptop} {
    gap: 20px;
  }
`;

const NavItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})`
  position: ${props => props.fullWidth ? 'static' : 'relative'};
`;

const NavLink = styled(Link)`
  color: #000000;
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  padding: 8px 0;
  transition: color 0.3s ease;
  
  &:hover {
    color: #2563EB;
    text-decoration: none;
  }
`;

const DropdownTrigger = styled.div`
  color: #000000;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: #2563EB;
  }
  
  &::after {
    content: '▼';
    font-size: 10px;
  }
`;

const NavItemWrapper = styled.div`
  position: relative;
`;

const NavItemTrigger = styled.div`
  color: #000000;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: #2563EB;
  }
  
  &::after {
    content: '▼';
    font-size: 10px;
  }
`;

const MegaMenuDropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'show',
})`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-top: 1px solid #E5E7EB;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 999;
  padding: 40px 0;
  margin-top: 0;
`;

const RegularDropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'show' && prop !== 'wide',
})`
  position: absolute;
  top: 100%;
  left: ${props => props.wide ? '-200px' : '0'};
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 999;
  padding: ${props => props.wide ? '24px' : '12px 0'};
  margin-top: 8px;
  min-width: ${props => props.wide ? '800px' : '200px'};
  max-width: ${props => props.wide ? '900px' : 'auto'};
`;

const DropdownMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'fullWidth' && prop !== 'wide',
})`
  background: white;
  border-radius: ${props => props.fullWidth ? '0' : '8px'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: ${props => props.fullWidth ? '40px 0' : '12px 0'};
  min-width: ${props => props.wide && !props.fullWidth ? '400px' : 'auto'};
  border-top: ${props => props.fullWidth ? '1px solid #E5E7EB' : 'none'};
  animation: fadeIn 0.2s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 12px 20px;
  color: #000000;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: #F3F4F6;
    color: #2563EB;
    text-decoration: none;
  }
`;

const DropdownItemWithDesc = styled(Link)`
  display: block;
  padding: 24px 32px;
  color: #000000;
  text-decoration: none;
  transition: all 0.3s ease;
  border-bottom: 1px solid #F3F4F6;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #F9FAFB;
    text-decoration: none;
  }
`;

const DropdownItemWithDescAnchor = styled.a`
  display: block;
  padding: 24px 32px;
  color: #000000;
  text-decoration: none;
  transition: all 0.3s ease;
  border-bottom: 1px solid #F3F4F6;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #F9FAFB;
    text-decoration: none;
  }
`;

const DropdownItemTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 8px;
  
  ${DropdownItemWithDesc}:hover &,
  ${DropdownItemWithDescAnchor}:hover & {
    color: #2563EB;
  }
`;

const DropdownItemDescription = styled.div`
  font-size: 15px;
  color: #000000;
  line-height: 1.6;
  max-width: 600px;
`;

const DropdownGrid = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'compact',
})`
  display: grid;
  grid-template-columns: ${props => props.compact ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))'};
  grid-template-rows: ${props => props.compact ? 'auto' : 'repeat(2, auto)'};
  grid-auto-flow: ${props => props.compact ? 'row' : 'column'};
  gap: 0;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    grid-auto-flow: row;
  }
`;

const MenuSectionTitle = styled.div`
  grid-column: 1 / -1;
  padding: 16px 32px 24px;
  border-bottom: 1px solid #E5E7EB;
  margin-bottom: 8px;
`;

const SectionTitleText = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const SectionTitleLink = styled(Link)`
  font-size: 20px;
  font-weight: 700;
  color: #000000;
  margin: 0;
  text-decoration: none;
  transition: color 0.3s ease;
  display: block;
  
  &:hover {
    color: #2563EB;
    text-decoration: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-left: auto;
`;

const SignInButton = styled(Link)`
  color: #000000;
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  padding: 10px 24px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #F3F4F6;
    color: #2563EB;
    text-decoration: none;
  }
`;

const SignUpButton = styled(Link)`
  background: linear-gradient(135deg, #FFD700 0%, #FF6B35 100%);
  color: #0F2744;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  padding: 10px 24px;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
    background: linear-gradient(135deg, #FFE84D 0%, #FF8652 100%);
    color: #0F2744;
    text-decoration: none;
  }
`;

const MobileNavWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'menuOpen',
})`
  display: none;
  
  @media ${device.laptop} {
    display: contents;
    
    .navbar-toggler {
      order: 2;
      margin-left: auto;
      border: none;
      padding: 8px;
      
      &:focus {
        box-shadow: none;
      }
      
      .navbar-toggler-icon {
        background-image: none;
        position: relative;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        
        &::before,
        &::after {
          content: '';
          position: absolute;
          width: 24px;
          height: 2px;
          background-color: #000000;
          transition: all 0.3s ease;
        }
        
        &::before {
          transform: ${props => props.menuOpen ? 'rotate(45deg)' : 'translateY(-6px)'};
        }
        
        &::after {
          transform: ${props => props.menuOpen ? 'rotate(-45deg)' : 'translateY(6px)'};
        }
      }
      
      ${props => props.menuOpen && `
        .navbar-toggler-icon {
          &::before {
            transform: rotate(45deg);
          }
          &::after {
            transform: rotate(-45deg);
          }
        }
      `}
      
      ${props => !props.menuOpen && `
        .navbar-toggler-icon::before {
          box-shadow: 0 -8px 0 0 #000000;
        }
      `}
    }
    
    .navbar-collapse {
      position: fixed !important;
      top: 76px !important;
      left: 0 !important;
      right: 0 !important;
      width: 100vw !important;
      background: white;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      max-height: calc(100vh - 76px);
      overflow-y: auto;
      padding: 20px;
      margin: 0 !important;
      z-index: 999;
      order: 3;
    }
    
    .navbar-collapse:not(.show) {
      display: none !important;
    }
    
    .navbar-collapse.show {
      display: block !important;
    }
    
    .nav-link {
      padding: 10px 15px;
    }
  }
`;

const DesktopNavWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-left: 48px;
  
  @media ${device.laptop} {
    display: none;
  }
`;

const ModernHeader = () => {
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showSolutionsDropdown, setShowSolutionsDropdown] = useState(false);
  const [showResourcesDropdown, setShowResourcesDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileNavClick = (to) => {
    setMobileMenuOpen(false);
    navigate(to);
  };

  return (
    <HeaderWrapper>
      <Container>
        <StyledNavbar expand="lg">
          <Navbar.Brand as={Link} to="/">
            <StaticImage src={'../images/AmetnesLogo-0f2744.png'} height={40} alt='Ametnes Logo' />
          </Navbar.Brand>
          
          <DesktopNavWrapper>
            <NavLinks>
              <NavItemWrapper
                onMouseEnter={() => setShowPlatformDropdown(true)}
                onMouseLeave={() => setShowPlatformDropdown(false)}
              >
                <NavItemTrigger>Platform</NavItemTrigger>
                <RegularDropdown show={showPlatformDropdown} wide style={{ minWidth: '800px', left: '-200px' }}>
                  <MenuSectionTitle>
                    <SectionTitleLink to="/platform" onClick={() => setShowPlatformDropdown(false)}>Platform giving you complete privacy and security</SectionTitleLink>
                  </MenuSectionTitle>
                  <DropdownGrid style={{ gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'repeat(3, auto)', gridAutoFlow: 'column' }}>
                    <DropdownItemWithDesc to="/platform/how-it-works" onClick={() => setShowPlatformDropdown(false)}>
                      <DropdownItemTitle>How It Works</DropdownItemTitle>
                      <DropdownItemDescription>
                        Learn how Ametnes deploys enterprise software in your infrastructure in 15 minutes
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/platform/public-cloud" onClick={() => setShowPlatformDropdown(false)}>
                      <DropdownItemTitle>Ametnes-Hosted Cloud</DropdownItemTitle>
                      <DropdownItemDescription>
                        Let Ametnes host and manage open source software for you in isolated environments with cost savings and zero infrastructure overhead
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/platform/private-cloud" onClick={() => setShowPlatformDropdown(false)}>
                      <DropdownItemTitle>Private Cloud & On-Premise</DropdownItemTitle>
                      <DropdownItemDescription>
                        Ametnes-managed open source in YOUR infrastructure. Complete control, air-gapped security, 90% cost savings
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/platform/enterprise-data" onClick={() => setShowPlatformDropdown(false)}>
                      <DropdownItemTitle>Enterprise Data</DropdownItemTitle>
                      <DropdownItemDescription>
                        Cost-effective open source data stack. SQL, NoSQL, Data Lakes, Vector DBs - privately managed with maximum security
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/platform/business-software" onClick={() => setShowPlatformDropdown(false)}>
                      <DropdownItemTitle>Business Software</DropdownItemTitle>
                      <DropdownItemDescription>
                        Cost-effective open source business stack. ERP, HRMS, Inventory, Finance - privately managed with 90% cost savings
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/platform/ai-software" onClick={() => setShowPlatformDropdown(false)}>
                      <DropdownItemTitle>AI Software</DropdownItemTitle>
                      <DropdownItemDescription>
                        Cost-effective open source AI stack. Private LLMs, AI Agents, MCPs, Workflows - privately managed with 95% cost savings
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                  </DropdownGrid>
                </RegularDropdown>
              </NavItemWrapper>
              
              <NavItemWrapper
                onMouseEnter={() => setShowSolutionsDropdown(true)}
                onMouseLeave={() => setShowSolutionsDropdown(false)}
              >
                <NavItemTrigger>Solutions</NavItemTrigger>
                <RegularDropdown show={showSolutionsDropdown} wide style={{ minWidth: '700px', left: '-150px' }}>
                  <MenuSectionTitle>
                    <SectionTitleLink to="/solutions/catalogue" onClick={() => setShowSolutionsDropdown(false)}>Solutions for every Enterprise challenge</SectionTitleLink>
                  </MenuSectionTitle>
                  <DropdownGrid style={{ gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'repeat(3, auto)', gridAutoFlow: 'column' }}>
                    <DropdownItemWithDesc to="/solutions/ai" onClick={() => setShowSolutionsDropdown(false)}>
                      <DropdownItemTitle>AI Solutions</DropdownItemTitle>
                      <DropdownItemDescription>
                        Intelligent automation and AI-powered business applications
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/solutions/data" onClick={() => setShowSolutionsDropdown(false)}>
                      <DropdownItemTitle>Data & Analytics</DropdownItemTitle>
                      <DropdownItemDescription>
                        Modern data platforms and advanced analytics solutions
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/solutions/automation" onClick={() => setShowSolutionsDropdown(false)}>
                      <DropdownItemTitle>Automation</DropdownItemTitle>
                      <DropdownItemDescription>
                        Workflow automation and process optimization
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/solutions/devops" onClick={() => setShowSolutionsDropdown(false)}>
                      <DropdownItemTitle>Developer</DropdownItemTitle>
                      <DropdownItemDescription>
                        CI/CD pipelines and infrastructure automation
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/solutions/enterprise" onClick={() => setShowSolutionsDropdown(false)}>
                      <DropdownItemTitle>Enterprise</DropdownItemTitle>
                      <DropdownItemDescription>
                        Scalable solutions for large organizations
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/solutions/sme" onClick={() => setShowSolutionsDropdown(false)}>
                      <DropdownItemTitle>SME</DropdownItemTitle>
                      <DropdownItemDescription>
                        Tailored solutions for small and medium enterprises
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                  </DropdownGrid>
                </RegularDropdown>
              </NavItemWrapper>

              <NavItem>
                <NavLink to="/pricing">Pricing</NavLink>
              </NavItem>

              <NavItemWrapper
                onMouseEnter={() => setShowResourcesDropdown(true)}
                onMouseLeave={() => setShowResourcesDropdown(false)}
              >
                <NavItemTrigger>Resources</NavItemTrigger>
                <RegularDropdown show={showResourcesDropdown} wide style={{ minWidth: '700px', left: '-150px' }}>
                  <MenuSectionTitle>
                    <SectionTitleLink to="/resources" onClick={() => setShowResourcesDropdown(false)}>Documentation, architectures, and updates</SectionTitleLink>
                  </MenuSectionTitle>
                  <DropdownGrid style={{ gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'repeat(2, auto)', gridAutoFlow: 'column' }}>
                    <DropdownItemWithDescAnchor href="/docs/" onClick={() => setShowResourcesDropdown(false)}>
                      <DropdownItemTitle>Docs</DropdownItemTitle>
                      <DropdownItemDescription>
                        Product guides, API references, and how-to content for deploying and operating Ametnes.
                      </DropdownItemDescription>
                    </DropdownItemWithDescAnchor>
                    <DropdownItemWithDesc to="/resources/architectures" onClick={() => setShowResourcesDropdown(false)}>
                      <DropdownItemTitle>Architectures</DropdownItemTitle>
                      <DropdownItemDescription>
                        Architecture center with patterns for observability, enterprise AI, and related platform topics.
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/blog" onClick={() => setShowResourcesDropdown(false)}>
                      <DropdownItemTitle>Blog</DropdownItemTitle>
                      <DropdownItemDescription>
                        Partnership announcements, platform news, and perspectives on enterprise open source.
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/events" onClick={() => setShowResourcesDropdown(false)}>
                      <DropdownItemTitle>Events &amp; webinars</DropdownItemTitle>
                      <DropdownItemDescription>
                        Upcoming sessions on private infrastructure across data, analytics, AI, systems monitoring and management, and security.
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                  </DropdownGrid>
                </RegularDropdown>
              </NavItemWrapper>
              
              <NavItemWrapper
                onMouseEnter={() => setShowCompanyDropdown(true)}
                onMouseLeave={() => setShowCompanyDropdown(false)}
              >
                <NavItemTrigger>Company</NavItemTrigger>
                <RegularDropdown show={showCompanyDropdown} wide>
                  <DropdownGrid compact>
                    <DropdownItemWithDesc to="/about" onClick={() => setShowCompanyDropdown(false)}>
                      <DropdownItemTitle>About Ametnes</DropdownItemTitle>
                      <DropdownItemDescription>
                        Meet our leadership team and understand our mission
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/careers" onClick={() => setShowCompanyDropdown(false)}>
                      <DropdownItemTitle>Careers</DropdownItemTitle>
                      <DropdownItemDescription>
                        Join our team and help shape the future of cloud infrastructure
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                    <DropdownItemWithDesc to="/contact" onClick={() => setShowCompanyDropdown(false)}>
                      <DropdownItemTitle>Contact Us</DropdownItemTitle>
                      <DropdownItemDescription>
                        Find our offices or contact us electronically
                      </DropdownItemDescription>
                    </DropdownItemWithDesc>
                  </DropdownGrid>
                </RegularDropdown>
              </NavItemWrapper>
            </NavLinks>
            
            <ButtonGroup>
              <SignInButton to="/console/signin">Sign In</SignInButton>
              <SignUpButton to="/console/signup">Start Free</SignUpButton>
            </ButtonGroup>
          </DesktopNavWrapper>
          
          <MobileNavWrapper menuOpen={mobileMenuOpen}>
            <Navbar.Toggle 
              aria-controls="mobile-nav" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            />
            <Navbar.Collapse id="mobile-nav" in={mobileMenuOpen}>
              <Nav className="flex-column">
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/platform'); }} style={{ fontWeight: 600, padding: '8px 0', cursor: 'pointer' }}>Platform</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/platform/how-it-works'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>How It Works</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/platform/public-cloud'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Ametnes-Hosted Cloud</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/platform/private-cloud'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Private Cloud & On-Premise</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/platform/enterprise-data'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Enterprise Data</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/platform/business-software'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Business Software</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/platform/ai-software'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>AI Software</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/solutions/catalogue'); }} style={{ fontWeight: 600, marginTop: '8px', padding: '8px 0', cursor: 'pointer' }}>Solutions</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/solutions/ai'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>AI Solutions</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/solutions/data'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Data & Analytics</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/solutions/automation'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Automation</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/solutions/devops'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Developer</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/pricing'); }} style={{ padding: '8px 0', cursor: 'pointer' }}>Pricing</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/resources'); }} style={{ fontWeight: 600, marginTop: '8px', padding: '8px 0', cursor: 'pointer' }}>Resources</Nav.Link>
                <Nav.Link as="a" href="/docs/" onClick={() => setMobileMenuOpen(false)} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Docs</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/resources/architectures'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Architectures</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/blog'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Blog</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/events'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Events &amp; webinars</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/about'); }} style={{ fontWeight: 600, marginTop: '8px', padding: '8px 0', cursor: 'pointer' }}>Company</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/about'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>About Ametnes</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/careers'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Careers</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/contact'); }} style={{ paddingLeft: '16px', fontSize: '14px', padding: '6px 0 6px 16px', cursor: 'pointer' }}>Contact Us</Nav.Link>
                <hr style={{ margin: '12px 0', width: '100%' }} />
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/console/signin'); }} style={{ padding: '8px 0', cursor: 'pointer' }}>Sign In</Nav.Link>
                <Nav.Link onClick={(e) => { e.preventDefault(); handleMobileNavClick('/console/signup'); }} style={{ padding: '8px 0', cursor: 'pointer' }}>
                  <SignUpButton as="span" style={{ display: 'inline-block', padding: '8px 16px', fontSize: '14px' }}>Start Free</SignUpButton>
                </Nav.Link>
              </Nav>
            </Navbar.Collapse>
          </MobileNavWrapper>
        </StyledNavbar>
      </Container>
    </HeaderWrapper>
  );
};

export default ModernHeader;


